<template>
  <div data-test="passenger-info-container">
    <template v-if="isAuth">
      <ContactInfo
        :customerDetails="customerDetails"
        :passengerPhone="passengerPhone"
        :passengerFirstName="passengerFirstName"
        :passengerLastName="passengerLastName"
        :passengerEmail="passengerEmail"
        :isAnotherPassenger="isAnotherPassenger"
        :anotherPassengersList="anotherPassengersList"
        :opsZoneCountry="opsZoneCountry"
        :is-night-cards-support="!!appConfig.isNightCardsSupport"
        class="mb-8"
        data-test="contact-info-section"
        @updatePhone="updatePhone"
        @updateFirstName="updateFirstName"
        @updateLastName="updateLastName"
        @updateEmail="updateEmail"
        @updatePassenger="updatePassenger"
        @populateUserProfileInfo="populateUserProfileInfo"
      >
        <template v-if="$slots.anotherPassengerNightCard" #anotherPassengerNightCard>
          <slot name="anotherPassengerNightCard" />
        </template>
      </ContactInfo>
      <slot name="extendedCheckout" />
      <FlightDetails
        v-if="isRideToOrFromAirport && !appConfig.isNightCardsSupport"
        :flightDetails="flightDetails"
        data-test="flight-details-section"
        @updateFlightDetails="updateFlightDetails"
      />
      <PaymentSelection
        v-if="!isFreeTrip"
        :paymentType="paymentType"
        :paymentMethod="paymentMethod"
        :isOnBoardPaymentAllowed="isOnBoardPaymentAllowed"
        data-test="payment-selection-section"
        @updatePaymentType="updatePaymentType"
        @updatePaymentMethod="updatePaymentMethod"
      />
      <Comment
        :commentForOutward="commentForOutward"
        :commentForReturn="commentForReturn"
        :tripType="tripType"
        data-test="comment-section"
        @updateCommentForOutward="updateCommentForOutward"
        @updateCommentForReturn="updateCommentForReturn"
      />
    </template>
    <ContactInfo
      v-else
      :authToken="isAuth"
      :lang="lang"
      :userLocation="userLocation"
      :termsAndConditionsPageName="termsAndConditionsPageName"
      :privacyPolicyPageName="privacyPolicyPageName"
      data-test="guest-contact-info-section"
      @setAuthToken="setAuthToken"
      @populateUserProfileInfo="populateUserProfileInfo"
      @setGlobalLoading="setGlobalLoading"
      @trackGtmEventForRegisteredUser="$emit('trackGtmEventForRegisteredUser')"
      @trackGtmEventForNonRegisteredUser="
        $emit('trackGtmEventForNonRegisteredUser')
      "
      @trackGtmEventForUserCreatedByOperator="
        $emit('trackGtmEventForUserCreatedByOperator')
      "
    />
  </div>
</template>
<script lang="ts">
export default {
  name: 'SPassengerInfo',
};
</script>

<script setup lang="ts">
import ContactInfo from '@slg/web-customer-shared/src/components/checkout/passengerInfo/contactInfo/ContactInfo.vue';
import Comment from '@slg/web-customer-shared/src/components/checkout/passengerInfo/comment/Comment.vue';
import FlightDetails from '@slg/web-customer-shared/src/components/checkout/passengerInfo/flightDetails/FlightDetails.vue';
import PaymentSelection from '@slg/web-customer-shared/src/components/checkout/passengerInfo/paymentSelection/PaymentSelection.vue';

defineProps({
  isAuth: {
    type: Boolean,
    default: false,
  },
  isFreeTrip: {
    type: Boolean,
    default: false,
  },
  opsZoneCountry: {
    type: String,
    default: '',
  },
  userLocation: {
    type: String,
    default: '',
  },
  termsAndConditionsPageName: {
    type: String,
    default: null,
  },
  privacyPolicyPageName: {
    type: String,
    default: null,
  },
  isRideToOrFromAirport: {
    type: Boolean,
    default: false,
  },
  customerDetails: {
    type: Object,
    default: () => {},
  },
  passengerPhone: {
    type: String,
    default: '',
  },
  passengerFirstName: {
    type: String,
    default: '',
  },
  passengerLastName: {
    type: String,
    default: '',
  },
  passengerEmail: {
    type: String,
    default: '',
  },
  isAnotherPassenger: {
    type: Boolean,
    default: false,
  },
  flightDetails: {
    type: String,
    default: '',
  },
  paymentType: {
    type: String,
    default: '',
  },
  paymentMethod: {
    type: String,
    default: '',
  },
  isOnBoardPaymentAllowed: {
    type: Boolean,
    default: false,
  },
  commentForOutward: {
    type: String,
    default: '',
  },
  commentForReturn: {
    type: String,
    default: '',
  },
  tripType: {
    type: String,
    default: '',
  },
  appConfig: {
    type: Object,
    default: () => {},
  },
  lang: {
    type: String,
    default: null,
  },
  anotherPassengersList: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits([
  'updatePhone',
  'updateFirstName',
  'updateLastName',
  'updateEmail',
  'updatePassenger',
  'updateFlightDetails',
  'updatePaymentType',
  'updatePaymentMethod',
  'updateCommentForOutward',
  'updateCommentForReturn',
  'setAuthToken',
  'populateUserProfileInfo',
  'common/setGlobalLoading',
  'trackGtmEventForRegisteredUser',
  'trackGtmEventForNonRegisteredUser',
  'trackGtmEventForUserCreatedByOperator',
]);

function updatePhone(newValue) {
  emit('updatePhone', newValue);
}
function updateFirstName(newValue) {
  emit('updateFirstName', newValue);
}
function updateLastName(newValue) {
  emit('updateLastName', newValue);
}
function updateEmail(newValue) {
  emit('updateEmail', newValue);
}
function updatePassenger(newValue) {
  emit('updatePassenger', newValue);
}
function updateFlightDetails(newValue) {
  emit('updateFlightDetails', newValue);
}
function updatePaymentType(newValue) {
  emit('updatePaymentType', newValue);
}
function updatePaymentMethod(newValue) {
  emit('updatePaymentMethod', newValue);
}
function updateCommentForOutward(newValue) {
  emit('updateCommentForOutward', newValue);
}
function updateCommentForReturn(newValue) {
  emit('updateCommentForReturn', newValue);
}
function setAuthToken(authToken) {
  emit('setAuthToken', authToken);
}
function populateUserProfileInfo() {
  emit('populateUserProfileInfo');
}
function setGlobalLoading(isLoading) {
  emit('common/setGlobalLoading', isLoading);
}
</script>
